import { Suspense } from 'react';
import { useAtomValue } from 'jotai';
import {
  filteredPropertiesFamily,
  selectedPropertiesAtom,
} from '@/features/property-search';
import { PropertyTile } from './PropertyTile';
import type { Property } from '@/types/web/property';

interface PropertySearchDropdownProps {
  searchTerm: string;
  isHomePage: boolean;
  onPropertySelect: (property: Property) => void;
}

function PropertySearchResults({
  searchTerm,
  onPropertySelect,
}: PropertySearchDropdownProps) {
  const filteredProperties = useAtomValue(filteredPropertiesFamily(searchTerm));
  const selectedProperties = useAtomValue(selectedPropertiesAtom);

  if (filteredProperties.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-gray-500">
        {searchTerm.trim()
          ? 'No properties found.'
          : 'No properties available.'}
      </div>
    );
  }

  // Sort properties with selected ones first
  const sortedProperties = filteredProperties.sort((a, b) => {
    const aSelected = selectedProperties.some((p) => p.id === a.id);
    const bSelected = selectedProperties.some((p) => p.id === b.id);

    // Selected properties come first
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });

  return (
    <>
      {sortedProperties.map((property) => {
        const isSelected = selectedProperties.some((p) => p.id === property.id);

        return (
          <PropertyTile
            key={property.id}
            property={property}
            isSelected={isSelected}
            onClick={onPropertySelect}
          />
        );
      })}
    </>
  );
}

function PropertySearchDropdownLoading() {
  return (
    <div className="py-6 text-center text-sm text-gray-500">
      Searching properties...
    </div>
  );
}

export function PropertySearchDropdown({
  searchTerm,
  isHomePage,
  onPropertySelect,
}: PropertySearchDropdownProps) {
  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-lg">
      <div className="max-h-[300px] overflow-y-auto">
        <Suspense fallback={<PropertySearchDropdownLoading />}>
          <PropertySearchResults
            searchTerm={searchTerm}
            isHomePage={isHomePage}
            onPropertySelect={onPropertySelect}
          />
        </Suspense>
      </div>
    </div>
  );
}
